import { useState, type ReactNode } from "react";
import { clockTime, phaseLabel } from "../../lib/labels.js";
import { outputWindow, stepDetail, stepTone } from "../../lib/stepPresentation.js";
import { logGrowth, useStickToBottom } from "../../lib/useStickToBottom.js";
import type { Step, StepIo } from "../../lib/viewModel.js";

/**
 * The full account of the build, in this skin's hand.
 *
 * Not a narrowing of the transcript: every step, with its clock time, its
 * repeat count and how long it has been going, the files a burst wrote, and the
 * command it ran with everything that command printed. Phase crossings are
 * section headings, the end of the feed says so out loud, and the whole thing
 * scrolls inside itself.
 *
 * It matters more here than in either other skin. This skin's spec panel is
 * honest to the point of being quiet — three of its four nodes can only say
 * "building" for minutes at a time, because the real counts do not exist until
 * the deploy is read. The log is what fills that silence with evidence, and at a
 * booth the evidence is the entire pitch: the visitor is being shown that a real
 * backend is really being written, and this is where they can watch it happen.
 *
 * The follow-to-bottom behaviour is `lib/useStickToBottom`'s, shared with both
 * other skins. Nothing about being stuck to the bottom of a log is skin, and a
 * second hand-written copy would be a second set of the three timing bugs that
 * module records.
 */
export function BuildLog({
  steps,
  isTerminal,
  fileCount,
}: {
  steps: Step[];
  isTerminal: boolean;
  fileCount: number;
}) {
  const { scrollerRef, following, scrolled, jumpToLatest } = useStickToBottom(
    logGrowth(steps, isTerminal),
  );

  const lastIndex = steps.length - 1;

  return (
    // `min-h-0` is load-bearing all the way down this chain. A flex child
    // defaults to a content-derived minimum size and refuses to shrink below it,
    // which makes the scroller's `overflow-y-auto` do nothing at all and turns
    // the whole screen back into an unbounded page. It is the single most
    // repeated layout bug in this codebase.
    <section
      aria-label="Build log"
      className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl border border-deck-line bg-deck-raise/50"
    >
      <header className="flex shrink-0 items-center justify-between gap-4 border-b border-deck-line px-4 py-2.5">
        <h2 className="font-deck-mono text-[10px] font-medium tracking-[0.16em] text-deck-text-faint uppercase">
          Build log
        </h2>
        {/* Totals, not what is on screen — otherwise a long build reports
            "4 steps" forever. Two elements rather than one string so each
            number is a thing on its own, which is also how they are read. */}
        <div className="flex shrink-0 items-baseline gap-1.5 font-deck-mono text-[10.5px] tabular-nums text-deck-text-faint">
          {fileCount > 0 && (
            <>
              <span>
                {fileCount} {fileCount === 1 ? "file" : "files"}
              </span>
              <span aria-hidden>&middot;</span>
            </>
          )}
          <span>
            {steps.length} {steps.length === 1 ? "step" : "steps"}
          </span>
        </div>
      </header>

      {/* The fades and the jump control are positioned against this box rather
          than the card, so neither can ride up over the header. */}
      <div className="relative flex min-h-0 flex-1 flex-col">
        <div
          ref={scrollerRef}
          // The generous bottom padding is the point: the last row gets clear
          // space under it instead of stopping against the frame, which is what
          // makes a finished feed look truncated.
          className="min-h-0 flex-1 overflow-y-auto px-2.5 pt-2 pb-14"
          role="log"
          aria-live="polite"
          aria-label="Build activity"
        >
          <ul className="space-y-0.5">
            {steps.map((step, i) => (
              <li key={step.id}>
                <Row step={step} active={i === lastIndex && !isTerminal} />
              </li>
            ))}
          </ul>
          {steps.length === 0 ? (
            // A build's first step can be twenty seconds away, and on a phone
            // this pane is half the screen. Empty, it reads as something that
            // failed to load rather than as something that has not started.
            <p className="px-2 py-3 font-deck-mono text-[11px] text-deck-text-faint">
              Waiting for the first step&hellip;
            </p>
          ) : (
            <Terminus isTerminal={isTerminal} />
          )}
        </div>

        {/* Both fades sit over the scroller and are inert, so they dim content
            at the edges without intercepting a scroll or a tap on a row. */}
        {scrolled && (
          <div
            aria-hidden
            className="pointer-events-none absolute inset-x-0 top-0 h-8 bg-linear-to-b from-deck-raise to-transparent"
          />
        )}
        {!following && (
          <>
            <div
              aria-hidden
              className="pointer-events-none absolute inset-x-0 bottom-0 h-20 bg-linear-to-t from-deck via-deck/80 to-transparent"
            />
            <button
              type="button"
              onClick={jumpToLatest}
              className="absolute bottom-3 left-1/2 flex h-11 -translate-x-1/2 cursor-pointer items-center gap-1.5 rounded-full border border-deck-line bg-deck-raise px-4 font-deck-mono text-[11px] tracking-[0.08em] text-deck-text-soft uppercase transition-colors hover:border-deck-blue hover:text-deck-text"
            >
              Jump to latest
              <span aria-hidden className="leading-none">
                &darr;
              </span>
            </button>
          </>
        )}
      </div>
    </section>
  );
}

/**
 * The bottom of the feed, said out loud.
 *
 * Without it the last row is the last thing on screen and there is no way to
 * tell a finished build from a clipped one. Live it doubles as the "still
 * running" cue, which is why the wording differs rather than the marker just
 * disappearing when the build ends.
 */
function Terminus({ isTerminal }: { isTerminal: boolean }) {
  return (
    <p className="mt-3 flex items-center gap-3 px-2 font-deck-mono text-[10px] tracking-[0.16em] text-deck-text-faint uppercase">
      {isTerminal ? (
        <>
          <span aria-hidden className="h-px flex-1 bg-deck-line" />
          End of build
          <span aria-hidden className="h-px flex-1 bg-deck-line" />
        </>
      ) : (
        <>
          <span
            aria-hidden
            className="size-1.5 rounded-full bg-deck-blue animate-[deckPulse_1.4s_ease-in-out_infinite]"
          />
          Working
        </>
      )}
    </p>
  );
}

function Row({ step, active }: { step: Step; active: boolean }) {
  // A phase crossing is a section heading, not an event — the same reading both
  // other skins give it. Without one, "Deploying" arrives in the feed as just
  // another sentence and the log stops saying where in the build it is.
  if (step.kind === "phase") {
    return (
      <p className="flex items-center gap-3 py-2.5 font-deck-mono text-[10px] tracking-[0.16em] text-deck-text-faint uppercase">
        <span aria-hidden className="h-px flex-1 bg-deck-line" />
        {phaseLabel(step.label)}
        <span aria-hidden className="h-px flex-1 bg-deck-line" />
      </p>
    );
  }

  // Which of these a step is, is not this skin's decision — see
  // lib/stepPresentation. What the colours are, is.
  const tone = stepTone(step);
  const failed = tone === "failed";
  const succeeded = tone === "succeeded";

  return (
    <div className={`flex gap-2.5 rounded-lg px-2 py-1.5 ${active ? "bg-deck-blue-soft" : ""}`}>
      {/* Unlike the hatch palette, this one has a red of its own, so "here and
          now" and "this failed" never have to share a colour. */}
      <span className="flex w-2.5 shrink-0 justify-center pt-[7px]" aria-hidden>
        <span
          className={`size-1.5 rounded-full ${
            failed
              ? "bg-deck-halt"
              : succeeded
                ? "bg-deck-live"
                : active
                  ? "bg-deck-blue animate-[deckPulse_1.4s_ease-in-out_infinite]"
                  : "bg-deck-line"
          }`}
        />
      </span>

      <div className="min-w-0 flex-1">
        <p
          className={`text-[13px] leading-snug ${
            failed
              ? "text-deck-halt"
              : succeeded
                ? "font-semibold text-deck-live"
                : "text-deck-text"
          }`}
        >
          {step.label}
        </p>

        <Detail step={step} active={active} />
        {step.io && <Io io={step.io} />}
      </div>

      <span className="shrink-0 pt-1 font-deck-mono text-[10.5px] tabular-nums text-deck-text-faint">
        {clockTime(step.at)}
      </span>
    </div>
  );
}

/** The secondary line: how many times, how long, and which files. */
function Detail({ step, active }: { step: Step; active: boolean }) {
  const detail = stepDetail(step, active);
  if (!detail) return null;

  return (
    <p className="mt-0.5 flex flex-wrap items-baseline gap-x-2 font-deck-mono text-[10.5px] text-deck-text-faint">
      {detail.parts.length > 0 && <span>{detail.parts.join(" · ")}</span>}
      {detail.paths.length > 0 && (
        <span className="min-w-0 truncate">
          {detail.paths.join(", ")}
          {detail.hiddenPaths > 0 && ` +${detail.hiddenPaths}`}
        </span>
      )}
    </p>
  );
}

/**
 * A step's command and what it printed, as one block beneath the row.
 *
 * Both halves are labelled in a gutter rather than by punctuation, so a command
 * is distinguishable from its output at a glance even when both are shell text.
 */
function Io({ io }: { io: StepIo }) {
  const [expanded, setExpanded] = useState(false);
  const { shown, hidden } = outputWindow(io, expanded);

  return (
    <div className="mt-1.5 overflow-hidden rounded-md border border-deck-line bg-deck-sunk">
      {io.input !== undefined && (
        <IoRow label="in">
          <span className="text-deck-text-soft">{io.input}</span>
        </IoRow>
      )}

      {io.output.length > 0 && (
        <IoRow label="out" divided={io.input !== undefined}>
          {hidden > 0 && (
            // Full-width and 44px tall. It is dense text around a real control,
            // and a control the size of the type beside it is one nobody can hit
            // on the screen this skin is built for.
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              className="-mx-1 mb-1 flex h-11 w-[calc(100%+0.5rem)] cursor-pointer items-center px-1 text-[10.5px] text-deck-text-faint transition-colors hover:text-deck-blue-hi"
            >
              {expanded ? "Show less" : `+${hidden} earlier ${hidden === 1 ? "line" : "lines"}`}
            </button>
          )}
          {/*
            Every line reads the same, whichever stream it came out of. Painting
            `stderr` as an error is simply wrong on a healthy build: npm writes
            its progress there, Vite writes its build report there, and the
            deploy prints its expiry notice there. What a step's outcome actually
            was is already said where it is known — the step's own dot and label
            — and that is the only place that can say it honestly.
          */}
          {shown.map((line) => (
            <div key={line.seq} className="text-deck-text-faint">
              {line.line}
            </div>
          ))}
        </IoRow>
      )}
    </div>
  );
}

function IoRow({
  label,
  divided,
  children,
}: {
  label: string;
  divided?: boolean;
  children: ReactNode;
}) {
  return (
    <div className={`flex gap-2.5 px-2.5 py-1.5 ${divided ? "border-t border-deck-line" : ""}`}>
      <span className="w-5 shrink-0 pt-px font-deck-mono text-[9.5px] tracking-[0.1em] text-deck-text-faint uppercase">
        {label}
      </span>
      {/* `min-w-0` so an unbroken 300-character line wraps instead of widening
          the pane and forcing the whole screen to scroll sideways. */}
      <div className="min-w-0 flex-1 font-deck-mono text-[10.5px] leading-relaxed break-all whitespace-pre-wrap">
        {children}
      </div>
    </div>
  );
}
